import type { ProfitReport } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { addDays, formatMoney, parseTyped } from '../format';
import { useAuth } from '../auth';
import { useDesktop, useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const RANGES = presets('d30', 'd90', 'd365');

type Row = ProfitReport['rows'][number];
type SortKey = 'name' | 'revenue' | 'cost' | 'margin' | 'pct' | 'known' | 'invoices' | 'lines' | 'stops' | 'fill' | 'shrink' | 'net' | 'retail' | 'retailMargin';

/** Who is making money: revenue, cost and margin by customer or by item over a range (owner/office). */
export function ProfitPage() {
  const desktop = useDesktop();
  return desktop ? <ProfitSheet /> : <ProfitList />;
}

/** Profitability as a report sheet: a row per customer or item with revenue, share, cost, margin and GM %. */
function ProfitSheet() {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const [byRaw, setBy] = useSearchParam('by');
  const by = byRaw === 'item' ? 'item' : 'customer';
  const period = usePeriod(RANGES);
  const query = useQuery({ queryKey: qk.profit(by, period.from, period.to), queryFn: ({ signal }) => api.profit({ by, from: period.from, to: period.to }, signal), placeholderData: keepPreviousData });
  const sorters = useMemo(
    () => ({
      name: byText<Row>((r) => r.name),
      revenue: byNumber<Row>((r) => r.revenue),
      cost: byNumber<Row>((r) => r.cost),
      margin: byNumber<Row>((r) => r.margin),
      pct: byNumber<Row>((r) => r.marginPct),
      known: byNumber<Row>((r) => r.costCoverage),
      invoices: byNumber<Row>((r) => r.invoices),
      lines: byNumber<Row>((r) => r.lines),
      stops: byNumber<Row>((r) => r.stops),
      fill: byNumber<Row>((r) => r.fillRate),
      shrink: byNumber<Row>((r) => r.shrinkCost),
      net: byNumber<Row>((r) => r.netMargin),
      retail: byNumber<Row>((r) => r.retailRevenue),
      retailMargin: byNumber<Row>((r) => r.retailMargin),
    }),
    [],
  );
  const { sorted, head } = useSort<Row, SortKey>(sorters, { key: 'margin', dir: -1 }, ['revenue', 'cost', 'margin', 'pct', 'known', 'invoices', 'lines', 'stops', 'shrink', 'net', 'retail', 'retailMargin']);
  const money = (v: number | null) => (v === null ? '' : formatMoney(v, intlLocale));
  const href = (r: Row) => (by === 'customer' ? `/customers/${r.id}` : `/items/${r.id}`);

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('profitTitle')}</h1>
          <p className="muted">{t('profitHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <div className="seg" role="group" aria-label={t('groupBy')}>
          <button type="button" aria-pressed={by === 'customer'} onClick={() => setBy('customer')}>
            {t('customers')}
          </button>
          <button type="button" aria-pressed={by === 'item'} onClick={() => setBy('item')}>
            {t('navProducts')}
          </button>
        </div>
        <PeriodBar presets={RANGES} period={period} />
        {by === 'customer' ? <CostPerStop /> : null}
      </div>
      <QueryView query={query}>
        {(r) => {
          const rows = sorted(r.rows);
          const rank = new Map([...r.rows].sort((a, b) => (b.margin ?? -Infinity) - (a.margin ?? -Infinity)).map((x, i) => [x.id, i + 1]));
          const share = (x: Row) => (r.totals.revenue > 0 ? Math.round((x.revenue / r.totals.revenue) * 1000) / 10 : null);
          const columns: SheetColumn<Row>[] = [
            { key: 'rank', header: t('rankCol'), width: '44px', align: 'right', render: (x) => rank.get(x.id) ?? '' },
            { key: 'name', header: head('name', by === 'customer' ? t('customer') : t('itemCol')), label: t('name'), width: '220px', render: (x) => <Link to={href(x)}>{x.name}</Link> },
            { key: 'revenue', header: head('revenue', t('revenue')), label: t('revenue'), width: '112px', align: 'right', render: (x) => money(x.revenue) },
            { key: 'share', header: t('shareCol'), width: '72px', align: 'right', render: (x) => (share(x) === null ? '' : `${share(x)!.toFixed(1)}%`) },
            { key: 'cost', header: head('cost', t('cost')), label: t('cost'), width: '112px', align: 'right', render: (x) => money(x.cost) },
            { key: 'margin', header: head('margin', t('grossMarginCol')), label: t('grossMarginCol'), width: '112px', align: 'right', render: (x) => (x.margin === null ? '' : <strong>{money(x.margin)}</strong>), cellClass: (x) => (x.margin !== null && x.margin < 0 ? 'bad' : undefined) },
            { key: 'pct', header: head('pct', t('gmCol')), label: t('gmCol'), width: '72px', align: 'right', render: (x) => (x.marginPct === null ? '' : `${x.marginPct.toFixed(1)}%`), cellClass: (x) => (x.marginPct === null ? undefined : x.marginPct < 0 ? 'bad' : x.marginPct < 10 ? 'warn' : undefined) },
            { key: 'known', header: head('known', t('costKnownCol')), label: t('costKnownCol'), hint: t('costCoverageHint'), width: '92px', align: 'right', render: (x) => `${Math.round(x.costCoverage * 100)}%`, cellClass: (x) => (x.costCoverage < 0.98 ? 'warn' : undefined) },
            { key: 'invoices', header: head('invoices', t('invoicesCountCol')), label: t('invoicesCountCol'), width: '80px', align: 'right', render: (x) => String(x.invoices) },
            { key: 'lines', header: head('lines', t('linesCol')), label: t('linesCol'), width: '72px', align: 'right', render: (x) => String(x.lines) },
            // after delivery (by customer) and after shrink (by item): decision 111
            ...(by === 'customer'
              ? [
                  { key: 'stops', header: head('stops', t('stops')), label: t('stops'), width: '64px', align: 'right', render: (x: Row) => (x.stops === null ? '' : String(x.stops)) } as SheetColumn<Row>,
                  { key: 'fill', header: head('fill', t('fillRateCol')), label: t('fillRateCol'), hint: t('fillRateHint'), width: '72px', align: 'right', render: (x: Row) => (x.fillRate === null ? '' : `${Math.round(x.fillRate * 1000) / 10}%`), cellClass: (x: Row) => (x.fillRate !== null && x.fillRate < 0.95 ? 'warn' : undefined) } as SheetColumn<Row>,
                ]
              : [{ key: 'shrink', header: head('shrink', t('shrinkCol')), label: t('shrinkCol'), width: '96px', align: 'right', render: (x: Row) => (x.shrinkCost ? money(x.shrinkCost) : ''), cellClass: (x: Row) => (x.shrinkCost ? 'warn' : undefined) } as SheetColumn<Row>]),
            // the register's sales beside the invoices, never added into them (decision 156)
            ...(by === 'item' && r.retail
              ? [
                  { key: 'retail', header: head('retail', t('registerCol')), label: t('registerCol'), hint: t('registerHint'), width: '104px', align: 'right', render: (x: Row) => money(x.retailRevenue) } as SheetColumn<Row>,
                  { key: 'retailMargin', header: head('retailMargin', t('registerMarginCol')), label: t('registerMarginCol'), width: '112px', align: 'right', render: (x: Row) => money(x.retailMargin), cellClass: (x: Row) => (x.retailMargin !== null && x.retailMargin < 0 ? 'bad' : undefined) } as SheetColumn<Row>,
                ]
              : []),
            ...(by === 'item' || r.costPerStop > 0
              ? [{ key: 'net', header: head('net', by === 'customer' ? t('netAfterDeliveryCol') : t('netAfterShrinkCol')), label: by === 'customer' ? t('netAfterDeliveryCol') : t('netAfterShrinkCol'), width: '120px', align: 'right', render: (x: Row) => (x.netMargin === null ? '' : <strong>{money(x.netMargin)}</strong>), cellClass: (x: Row) => (x.netMargin !== null && x.netMargin < 0 ? 'bad' : undefined) } as SheetColumn<Row>]
              : []),
          ];
          const footer = (
            <tr>
              <td />
              <td>{t('reportTotal')}</td>
              <td className="al-right" data-testid="profit-revenue">{money(r.totals.revenue)}</td>
              <td className="al-right">{r.totals.revenue > 0 ? '100%' : ''}</td>
              <td className="al-right">{money(r.totals.cost)}</td>
              <td className="al-right" data-testid="profit-margin">
                <strong>{money(r.totals.margin)}</strong>
              </td>
              <td className="al-right">{r.totals.marginPct === null ? '' : `${r.totals.marginPct.toFixed(1)}%`}</td>
              <td className="al-right">{`${Math.round(r.totals.costCoverage * 100)}%`}</td>
              {/* by product an invoice sits in several rows, so the rows' invoice counts do not add up to the invoices */}
              <td className="al-right">{by === 'customer' ? r.rows.reduce((s, x) => s + x.invoices, 0) || '' : ''}</td>
              <td className="al-right">{r.rows.reduce((s, x) => s + x.lines, 0) || ''}</td>
              {by === 'customer' ? (
                <>
                  <td className="al-right">{r.rows.reduce((s, x) => s + (x.stops ?? 0), 0) || ''}</td>
                  <td />
                </>
              ) : (
                <td className="al-right">{money(r.rows.reduce((s, x) => s + (x.shrinkCost ?? 0), 0) || null)}</td>
              )}
              {by === 'item' && r.retail ? (
                <>
                  <td className="al-right">{money(r.retail.revenue)}</td>
                  <td className="al-right">{money(r.retail.margin)}</td>
                </>
              ) : null}
              {by === 'item' || r.costPerStop > 0 ? (
                <td className="al-right">
                  <strong>{money(r.totals.netMargin)}</strong>
                </td>
              ) : null}
            </tr>
          );
          return (
            <>
              <dl className="totals-strip" data-testid="profit-strip">
                <div>
                  <dt>{t('revenue')}</dt>
                  <dd>{money(r.totals.revenue)}</dd>
                </div>
                <div>
                  <dt>{t('cost')}</dt>
                  <dd>{money(r.totals.cost)}</dd>
                </div>
                <div>
                  <dt>{t('grossMarginCol')}</dt>
                  <dd>{money(r.totals.margin)}</dd>
                </div>
                <div>
                  <dt>{t('gmCol')}</dt>
                  <dd>{r.totals.marginPct === null ? '—' : `${r.totals.marginPct.toFixed(1)}%`}</dd>
                </div>
                <div className={r.totals.costCoverage < 0.98 ? 'warn' : undefined}>
                  <dt>{t('costKnownCol')}</dt>
                  <dd>{`${Math.round(r.totals.costCoverage * 100)}%`}</dd>
                </div>
              </dl>
              <MarginGaps report={r} />
              {rows.length === 0 ? (
                <p className="muted pick-first">{t('noResults')}</p>
              ) : (
                <Sheet label={t('profitTitle')} columns={columns} rows={rows} rowKey={(x) => x.id} rowLabel={(x) => x.name} minWidth="1240px" footer={footer} onOpen={(x) => navigate(href(x))} />
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

/** What one delivery stop costs (decision 111): the owner sets it, the margin by customer is shown after it. */
function CostPerStop() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: qk.marginSettings, queryFn: ({ signal }) => api.marginSettings(signal) });
  const [text, setText] = useState<string | null>(null);
  const owner = me?.role === 'owner';
  const value = text ?? (settings.data ? String(settings.data.costPerStop) : '');
  const n = parseTyped(value);
  const save = useMutation({
    mutationFn: () => api.saveMarginSettings({ costPerStop: Math.round((n ?? 0) * 100) / 100 }),
    onSuccess: (v) => {
      qc.setQueryData(qk.marginSettings, v);
      setText(null);
      void qc.invalidateQueries({ queryKey: ['reports', 'profit'] });
    },
  });
  if (!settings.data) return null;
  if (!owner) return settings.data.costPerStop > 0 ? <span className="muted">{t('costPerStop')}: {formatMoney(settings.data.costPerStop, intlLocale)}</span> : null;
  return (
    <form
      className="contact-actions"
      onSubmit={(e) => {
        e.preventDefault();
        if (n !== null && n >= 0 && n <= 10_000) save.mutate();
      }}
    >
      <label className="muted">
        {t('costPerStop')}{' '}
        <input type="text" inputMode="decimal" value={value} aria-label={t('costPerStop')} aria-invalid={n === null || n < 0 || n > 10_000 || undefined} onChange={(e) => setText(e.target.value)} style={{ width: 80 }} />
      </label>
      {text !== null ? (
        <button type="submit" className="btn btn-secondary" disabled={save.isPending || n === null || n < 0 || n > 10_000}>
          {t('save')}
        </button>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
    </form>
  );
}

/** The phone's profit list: the total card, then a row per customer or item. */
function ProfitList() {
  const { t, intlLocale } = useLocale();
  const [byRaw, setBy] = useSearchParam('by');
  // the same period as the desk (?from=&to=), so a linked period reads the same on a phone
  const { from, to, today, setRange } = usePeriod(RANGES);
  const by = byRaw === 'item' ? 'item' : 'customer';
  const query = useQuery({ queryKey: qk.profit(by, from, to), queryFn: ({ signal }) => api.profit({ by, from, to }, signal) });

  return (
    <div className="stack">
      <PageHead title={t('profitTitle')} back="/" />
      <p className="muted">{t('profitHint')}</p>
      <div className="chips" role="group" aria-label={t('period')}>
        {RANGES.map((r) => {
          const start = addDays(today, -r.days);
          return (
            <button key={r.key} type="button" className="chip" aria-pressed={from === start && to === today} onClick={() => setRange({ from: start, to: '' })}>
              {t(`range_${r.key}`)}
            </button>
          );
        })}
      </div>
      <div className="chips" role="group" aria-label={t('groupBy')}>
        <button type="button" className="chip" aria-pressed={by === 'customer'} onClick={() => setBy('customer')}>
          {t('customers')}
        </button>
        <button type="button" className="chip" aria-pressed={by === 'item'} onClick={() => setBy('item')}>
          {t('items')}
        </button>
      </div>
      <QueryView query={query}>
        {(r) => (
          <>
            <section className="card">
              <h2>{t('total')}</h2>
              <dl className="facts plain">
                <div className="fact">
                  <dt>{t('revenue')}</dt>
                  <dd className="money">{formatMoney(r.totals.revenue, intlLocale)}</dd>
                </div>
                <div className="fact">
                  <dt>{t('cost')}</dt>
                  <dd>{formatMoney(r.totals.cost, intlLocale)}</dd>
                </div>
                <div className="fact">
                  <dt>{t('margin')}</dt>
                  <dd className="money">
                    {formatMoney(r.totals.margin, intlLocale)}
                    {r.totals.marginPct !== null ? ` · ${r.totals.marginPct}%` : ''}
                  </dd>
                </div>
              </dl>
              {r.totals.costCoverage < 0.98 ? (
                <p className="note">
                  {t('costCoverage')}: {Math.round(r.totals.costCoverage * 100)}%. {t('costCoverageHint')}
                </p>
              ) : null}
            </section>
            <MarginGaps report={r} />
            {r.rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {r.rows.map((row, i) => (
                  <li key={row.id} className="row">
                    <span className="qty-pill">{i + 1}</span>
                    <span className="row-main">
                      <span className="row-title">{row.name}</span>
                      <span className="row-sub">
                        {formatMoney(row.revenue, intlLocale)} · {row.invoices} {t('invoicesCount')}
                        {row.costCoverage < 0.98 ? <span className="badge badge-warn">{Math.round(row.costCoverage * 100)}% {t('withCost')}</span> : null}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-label">{t('margin')}</span>
                      <span className="row-end-value">{row.margin === null ? '—' : formatMoney(row.margin, intlLocale)}</span>
                      <span className="row-sub">{row.marginPct === null ? t('noCost') : `${row.marginPct}%`}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}

/**
 * Why some sales have no margin, and the register's sales at cost (decision 156): the sales with no product (link them),
 * the products sold with no cost (no price that day, or no box content for a pound or each sale), the register.
 */
function MarginGaps({ report: r }: { report: ProfitReport }) {
  const { t, intlLocale } = useLocale();
  const money = (v: number) => formatMoney(v, intlLocale);
  const g = r.gaps;
  const anyGap = !!g && (g.noProduct.revenue > 0 || g.noCost.length > 0 || g.retailNoProduct.revenue > 0);
  if (!anyGap && !r.retail) return null;
  return (
    <div className="stack-sm margin-gaps">
      {r.retail ? (
        <section className="card stack-sm" aria-label={t('registerTitle')} data-testid="profit-register">
          <h2>{t('registerTitle')}</h2>
          <p className="muted small">{t('registerHint')}</p>
          <dl className="totals-strip">
            <div>
              <dt>{t('revenue')}</dt>
              <dd>{money(r.retail.revenue)}</dd>
            </div>
            <div>
              <dt>{t('cost')}</dt>
              <dd>{money(r.retail.cost)}</dd>
            </div>
            <div>
              <dt>{t('grossMarginCol')}</dt>
              <dd>{money(r.retail.margin)}</dd>
            </div>
            <div>
              <dt>{t('gmCol')}</dt>
              <dd>{r.retail.marginPct === null ? '—' : `${r.retail.marginPct.toFixed(1)}%`}</dd>
            </div>
            <div className={r.retail.costCoverage < 0.98 ? 'warn' : undefined}>
              <dt>{t('costKnownCol')}</dt>
              <dd>{`${Math.round(r.retail.costCoverage * 100)}%`}</dd>
            </div>
          </dl>
        </section>
      ) : null}
      {g && anyGap ? (
        <section className="card stack-sm" aria-label={t('gapsTitle')} data-testid="profit-gaps">
          <h2>{t('gapsTitle')}</h2>
          <p className="muted small">{t('gapsHint')}</p>
          <ul className="gap-list">
            {g.noProduct.revenue > 0 ? (
              <li>
                <strong>{money(g.noProduct.revenue)}</strong> {t('gapNoProduct').replace('{n}', String(g.noProduct.lines))}{' '}
                <Link to="/items/linking">{t('gapLinkSheet')}</Link> · <Link to="/qbo/matching">{t('gapLinkMatching')}</Link> · <Link to="/items/inbox">{t('gapLinkInbox')}</Link>
              </li>
            ) : null}
            {g.retailNoProduct.revenue > 0 ? (
              <li>
                <strong>{money(g.retailNoProduct.revenue)}</strong> {t('gapRegisterNoProduct').replace('{n}', String(g.retailNoProduct.lines))} <Link to="/items/inbox">{t('gapLinkInbox')}</Link>
              </li>
            ) : null}
          </ul>
          {g.noCost.length ? (
            <table className="mini-table">
              <thead>
                <tr>
                  <th>{t('itemCol')}</th>
                  <th className="al-right">{t('revenue')}</th>
                  <th>{t('gapWhy')}</th>
                </tr>
              </thead>
              <tbody>
                {g.noCost.map((x) => (
                  <tr key={x.itemId}>
                    <td>
                      <Link to={`/items/${x.itemId}${x.reason === 'no_price' ? '' : '?edit=1'}`}>{x.itemName}</Link>
                    </td>
                    <td className="al-right">{money(x.revenue)}</td>
                    <td className="wrap">{x.reason === 'no_content' ? t('gapNoContent') : x.reason === 'no_convert' ? t('gapNoConvert') : t('gapNoPrice')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {g.noCost.some((x) => x.reason === 'no_content') ? (
            <p className="small">
              <Link to="/items/linking">{t('gapFillBoxes')}</Link>
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
